import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ScrollToTop from '../../components/ScrollTop';
import PageBanner from '../../components/PageBanner';
import './gallery.scss';

import { getGallery } from '../../services/data.service';
import assetUrl from '../../services/assetUrl';

const PHOTOS_PER_ROW = 4;
const INITIAL_ROWS = 4;
const INITIAL_VISIBLE = PHOTOS_PER_ROW * INITIAL_ROWS;

const ExpandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 8v-2a2 2 0 0 1 2 -2h2"></path>
    <path d="M4 16v2a2 2 0 0 0 2 2h2"></path>
    <path d="M16 4h2a2 2 0 0 1 2 2v2"></path>
    <path d="M16 20h2a2 2 0 0 0 2 -2v-2"></path>
  </svg>
);

const normalizeCategory = (rawCat, rawYear) => {
  if (!rawCat) return { mainCategory: "Campus Life", year: rawYear || "" };
  const trimmed = rawCat.trim();
  
  // Extract trailing 4-digit year if embedded in category name like "Graduation Day 2023", "IKYA 2026", "MBA Graduation Day 2026", "Yoga Day 2024"
  const match = trimmed.match(/^(.*?)(?:\s+(\d{4}))$/i);
  if (match) {
    return {
      mainCategory: match[1].trim(),
      year: rawYear || match[2]
    };
  }
  return {
    mainCategory: trimmed,
    year: rawYear || ""
  };
};

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [activeYear, setActiveYear] = useState("All");
  const [openDropdownTab, setOpenDropdownTab] = useState(null);
  const [popupIndex, setPopupIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getGallery();
        if (res && res.success) {
          const items = res.data.map(item => {
            const { mainCategory, year } = normalizeCategory(item.category, item.year);
            return {
              ...item,
              rawCategory: item.category,
              mainCategory,
              year,
              src: assetUrl(item.imageUrl)
            };
          });
          setGalleryItems(items);
        }
      } catch (err) {
        console.error("Failed to load gallery items", err);
      }
    };
    fetchData();
  }, []);

  // Close tab dropdown on outside click
  useEffect(() => {
    const handleDocClick = () => setOpenDropdownTab(null);
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, []);

  // Collect unique main categories
  const mainCategories = [...new Set(galleryItems.map(item => item.mainCategory))];
  
  // Build categories map with available years and counts
  const categoryMeta = {};
  mainCategories.forEach(cat => {
    const catItems = galleryItems.filter(it => it.mainCategory.toLowerCase() === cat.toLowerCase());
    const years = [...new Set(catItems.map(it => it.year).filter(y => y && String(y).trim()))].sort((a, b) => b - a);
    categoryMeta[cat] = {
      totalCount: catItems.length,
      years,
      hasDropdown: years.length > 0
    };
  });

  const tabs = ["All", ...mainCategories];

  // Filtering
  const displayedImages = galleryItems.filter(item => {
    if (activeTab === "All") return true;
    if (item.mainCategory.toLowerCase() !== activeTab.toLowerCase()) return false;
    if (activeYear !== "All" && String(item.year) !== String(activeYear)) return false;
    return true;
  });

  const visibleImages = displayedImages.slice(0, visibleCount);
  const canLoadMore = visibleCount < displayedImages.length;
  const canLoadLess = visibleCount > INITIAL_VISIBLE;

  const selectTab = (tab, year = "All", e) => {
    if (e) e.stopPropagation();
    setPopupIndex(null);
    setActiveTab(tab);
    setActiveYear(year);
    setVisibleCount(INITIAL_VISIBLE);
    setOpenDropdownTab(null);
  };

  const toggleDropdown = (tab, e) => {
    e.stopPropagation();
    if (openDropdownTab === tab) {
      setOpenDropdownTab(null);
    } else {
      setOpenDropdownTab(tab);
    }
  };

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + INITIAL_VISIBLE, displayedImages.length));
  };

  const loadLess = () => {
    const nextCount = Math.max(INITIAL_VISIBLE, visibleCount - INITIAL_VISIBLE);
    setVisibleCount(nextCount);
    if (popupIndex !== null && popupIndex >= nextCount) {
      setPopupIndex(null);
    }
  };

  const closePopup = useCallback(() => setPopupIndex(null), []);
  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setPopupIndex((prev) => (prev > 0 ? prev - 1 : visibleImages.length - 1));
  }, [visibleImages.length]);
  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    setPopupIndex((prev) => (prev < visibleImages.length - 1 ? prev + 1 : 0));
  }, [visibleImages.length]);

  // Keyboard navigation + lock body scroll while the lightbox is open
  useEffect(() => {
    if (popupIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePopup();
      else if (e.key === "ArrowLeft") prevImage();
      else if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [popupIndex, closePopup, prevImage, nextImage]);

  return (
    <>
      <Header />

      {/* Global banner */}
      <PageBanner
        title="Campus Gallery"
        eyebrow="Memories"
        subtitle="Discover the vibrant moments that define our journey — grand events, cultural festivities, and proud academic milestones, all captured in a stunning visual collection."
      />

      <div className="gallery-page">
        <div className="gallery-container">

          {/* Filter Tabs with Integrated Year Dropdowns */}
          <div className="gallery-tabs">
            {/* "All" Tab */}
            <button
              type="button"
              className={`gallery-tab ${activeTab === "All" ? "active" : ""}`}
              onClick={(e) => selectTab("All", "All", e)}
            >
              {activeTab === "All" && (
                <motion.span
                  layoutId="galleryTabBg"
                  className="gallery-tab__bg"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )}
              <span className="gallery-tab__label">All</span>
              <span className="gallery-tab__count">{galleryItems.length}</span>
            </button>

            {/* Category Tabs */}
            {mainCategories.map((cat) => {
              const meta = categoryMeta[cat] || { totalCount: 0, years: [], hasDropdown: false };
              const isTabActive = activeTab === cat;
              const isDropdownOpen = openDropdownTab === cat;
              const currentCount = isTabActive && activeYear !== "All"
                ? galleryItems.filter(it => it.mainCategory.toLowerCase() === cat.toLowerCase() && String(it.year) === String(activeYear)).length
                : meta.totalCount;

              if (meta.hasDropdown) {
                return (
                  <div key={cat} className={`gallery-tab-wrapper ${isTabActive ? "is-active" : ""}`}>
                    <div className={`gallery-tab gallery-tab--dropdown ${isTabActive ? "active" : ""}`}>
                      {isTabActive && (
                        <motion.span
                          layoutId="galleryTabBg"
                          className="gallery-tab__bg"
                          transition={{ type: "spring", stiffness: 420, damping: 34 }}
                        />
                      )}
                      <span className="gallery-tab__label" onClick={(e) => selectTab(cat, "All", e)}>
                        {cat} {isTabActive && activeYear !== "All" ? `(${activeYear})` : ""}
                      </span>
                      <span className="gallery-tab__count" onClick={(e) => selectTab(cat, "All", e)}>
                        {currentCount}
                      </span>
                      <button
                        type="button"
                        className={`gallery-tab__arrow ${isDropdownOpen ? "open" : ""}`}
                        onClick={(e) => toggleDropdown(cat, e)}
                        aria-label={`Select year for ${cat}`}
                        title={`Select year for ${cat}`}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </div>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          className="gallery-tab-menu"
                          initial={{ opacity: 0, y: -8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.96 }}
                          transition={{ duration: 0.16 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className={`gallery-tab-menu__item ${isTabActive && activeYear === "All" ? "selected" : ""}`}
                            onClick={(e) => selectTab(cat, "All", e)}
                          >
                            <span>All Years</span>
                            <span className="badge">{meta.totalCount}</span>
                          </button>
                          {meta.years.map(yr => {
                            const countForYear = galleryItems.filter(it => it.mainCategory.toLowerCase() === cat.toLowerCase() && String(it.year) === String(yr)).length;
                            return (
                              <button
                                key={yr}
                                type="button"
                                className={`gallery-tab-menu__item ${isTabActive && activeYear === yr ? "selected" : ""}`}
                                onClick={(e) => selectTab(cat, yr, e)}
                              >
                                <span>{yr}</span>
                                <span className="badge">{countForYear}</span>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <button
                  key={cat}
                  type="button"
                  className={`gallery-tab ${isTabActive ? "active" : ""}`}
                  onClick={(e) => selectTab(cat, "All", e)}
                >
                  {isTabActive && (
                    <motion.span
                      layoutId="galleryTabBg"
                      className="gallery-tab__bg"
                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className="gallery-tab__label">{cat}</span>
                  <span className="gallery-tab__count">{meta.totalCount}</span>
                </button>
              );
            })}
          </div>

          {/* Image grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeTab}-${activeYear}`}
              className="gallery-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {visibleImages.map((item, index) => (
                <motion.button
                  type="button"
                  key={`${activeTab}-${activeYear}-${index}`}
                  className="gallery-card"
                  onClick={() => setPopupIndex(index)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "0px 0px -40px 0px" }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: (index % INITIAL_VISIBLE) * 0.03 }}
                >
                  <img src={item.src} alt={item.title || `${item.mainCategory} ${index + 1}`} loading="lazy" />
                  <span className="gallery-card__overlay">
                    <div className="gallery-card__info">
                      <span className="gallery-card__title">{item.title || item.rawCategory || item.mainCategory}</span>
                      {item.year && <span className="gallery-card__year">{item.year}</span>}
                    </div>
                    <span className="gallery-card__icon"><ExpandIcon /></span>
                  </span>
                </motion.button>
              ))}
            </motion.div>
          </AnimatePresence>

          {(canLoadMore || canLoadLess) && (
            <div className="gallery-actions">
              {canLoadLess && (
                <button type="button" className="gallery-actions__btn gallery-actions__btn--secondary" onClick={loadLess}>
                  Load Less
                </button>
              )}
              {canLoadMore && (
                <button type="button" className="gallery-actions__btn gallery-actions__btn--primary" onClick={loadMore}>
                  Load More
                </button>
              )}
            </div>
          )}
        </div>
        <ScrollToTop />
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {popupIndex !== null && (
          <motion.div
            className="gallery-lightbox"
            onClick={closePopup}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button className="gallery-lightbox__close" onClick={closePopup} aria-label="Close">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>

            <button className="gallery-lightbox__nav gallery-lightbox__nav--prev" onClick={prevImage} aria-label="Previous image">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>

            <motion.div
              className="gallery-lightbox__stage"
              onClick={(e) => e.stopPropagation()}
              key={popupIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <img src={visibleImages[popupIndex]?.src} alt={visibleImages[popupIndex]?.title || "Gallery full view"} />
            </motion.div>

            <button className="gallery-lightbox__nav gallery-lightbox__nav--next" onClick={nextImage} aria-label="Next image">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>

            <div className="gallery-lightbox__counter">
              {popupIndex + 1} / {visibleImages.length} <span>&bull;</span> {activeTab}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </>
  );
};

export default Gallery;
